import React, { useEffect, useState } from 'react';
import { DialogTitle, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchDepartements, setSelectedDepartement } from '../reduxStore/DepartementSlice';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { TableAction, TableHeader } from '../constants/interfaces';
import { ButtonType } from '../constants/enums';
import { Departement } from '../models/models';
import MyTable from '../components/MyTable';
import { useDialog } from '../components/DialogComponent';
import DepartementForm from '../components/Forms/DepartementForm';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import AppOutlineButton from '../components/layout/AppOutlineButton';

const GestionDepartement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { departements, loading, error, successMessage } = useSelector(
    (state: any) => state.DepartementSlice
  );
  const selectedDepartement = useSelector(
    (state: any) => state.DepartementSlice.selectedDepartement
  );
  const activeDepartement = useSelector((state: RootState) => state.AppSlice.selectedDepartement);
  const isAllMode         = useSelector((state: RootState) => state.AppSlice.isAllMode);
  const [fetchParams] = useState({ limit: 1000, offset: 0 });

  const [FormDialog, openDialog, closeDialog] = useDialog({
    dialogProps: { maxWidth: 'sm', fullWidth: true },
  });

  const handleCancel = () => {
    closeDialog();
    dispatch(setSelectedDepartement(null));
  };

  useEffect(() => {
    dispatch(fetchDepartements());
  }, [dispatch, fetchParams, successMessage]);

  useEffect(() => {
    // Redirect onboarding : après un save réussi, si l'utilisateur n'a
    // pas encore choisi de contexte (ni dept actif, ni mode ALL), retour
    // dashboard. En mode ALL, l'admin gère intentionnellement les depts —
    // pas de redirect.
    if (successMessage && !activeDepartement && !isAllMode) {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successMessage]);

  const headers: TableHeader<Departement>[] = [
    { key: 'nom', label: 'Nom' },
    { key: 'description', label: 'Description' },
    { key: 'depot_nom', label: 'Dépôt' },
  ];

  const tableActions: TableAction<Departement>[] = [
    {
      label: 'Edit',
      callback: (dep) => {
        dispatch(setSelectedDepartement(dep));
        openDialog();
      },
      type: ButtonType.EDIT,
    },
  ];

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title="Gestion des départements"
        subtitle={`${departements.length} département${departements.length > 1 ? 's' : ''}`}
        icon={<ApartmentIcon />}
        addLabel="Ajouter un département"
        onAdd={openDialog}
        actions={
          !activeDepartement ? (
            <AppOutlineButton
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
            >
              Sélection département
            </AppOutlineButton>
          ) : undefined
        }
      />

      {loading && <Typography color="#667085" mb={2}>Chargement...</Typography>}
      {error && <Typography color="#b42318" mb={2}>Erreur : {error}</Typography>}

      <AppSurface>
        <MyTable
          headers={headers}
          data={departements}
          actions={tableActions}
          totalRows={departements.length}
          onPageChange={() => {}}
        />
      </AppSurface>

      <FormDialog>
        <DialogTitle>
          <Typography variant="h6">
            {selectedDepartement ? 'Modifier un département' : 'Ajouter un département'}
          </Typography>
        </DialogTitle>
        <DepartementForm onCancel={handleCancel} />
      </FormDialog>
    </AppPage>
  );
};

export default GestionDepartement;
